// Copyright (c) Move Closer 2022.

import { IConnector, Injectable, IResponse, ResourceActionFailed, QueryParams } from '@movecloser/front-core'

import { logger, resolveFromStatus } from '../../../../support'
import {
  FilterParamConfig,
  IRelatedService,
  ISiteService, PossibleRelatedType,
  SearchGroup,
  SearchResultsConfig
} from '../../../../contexts'

import { TabsService } from '../tabs'
import { ISearchService, SearchConfig, SearchResultItemTab } from './contracts'
import { getCookie } from '../../helpers/getCookie'
import { SYNERISE_UUID_COOKIE_NAME } from '../../contracts/suggestedProducts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Filip Rurak <filip.rurak@movecloser.pl>  (edited)
 */
@Injectable()
export class SearchService extends TabsService<SearchResultItemTab> implements ISearchService {
  protected initialData: SearchGroup | null = null

  protected readonly connector: IConnector
  protected readonly relatedService: IRelatedService
  protected readonly siteService: ISiteService
  protected readonly config: SearchConfig | null

  constructor (connector: IConnector, relatedService: IRelatedService, siteService: ISiteService, config: SearchConfig | null) {
    super()
    this.connector = connector
    this.relatedService = relatedService
    this.siteService = siteService
    this.config = config
  }

  /**
   * @inheritDoc
   */
  public getConfig (): SearchResultsConfig {
    this.setConfig()
    return this._tabs.reduce((acc, nextTab) => {
      return {
        ...acc,
        ...nextTab.preview
      }
    }, {})
  }

  /**
   * @inheritDoc
   */
  public getInitialData (): SearchGroup {
    if (!this.initialData) {
      throw new Error('InitialData is null')
    }

    return this.initialData
  }

  /**
   * @inheritDoc
   */
  public async search (phrase: string, query: QueryParams): Promise<SearchGroup[]> {
    const shouldUseSynerise: boolean = this.config !== null && this.config.useSyneriseApi
    const syneriseCookie = getCookie(SYNERISE_UUID_COOKIE_NAME)

    const response: IResponse = await this.connector.call(
      shouldUseSynerise ? 'synerise' : 'shared',
      shouldUseSynerise ? 'syneriseSearch' : 'search',
      {},
      { q: phrase, _snrs_uuid: syneriseCookie, ...query }
    )

    if (!response.isSuccessful()) {
      throw new ResourceActionFailed(
        response.data.message,
        resolveFromStatus(response.status),
        {}
      )
    }

    this.relatedService.storeRelated(response.data.meta.related)

    const data = []

    for (const result of response.data.data.results) {
      const tabCandidate = this.getTabByValue(result.group)
      if (tabCandidate) {
        try {
          const resolvedGroup = await tabCandidate.resolver(result.group, result.list, this.relatedService)
          data.push(resolvedGroup)
        } catch (e) {
          logger(e, 'warn')
        }
      }
    }

    const resolvedFilters = SearchService.resolveFilters(response)

    if (resolvedFilters && resolvedFilters.length > 0) {
      data.push({
        elements: resolvedFilters,
        group: PossibleRelatedType.ProductFilter,
        totalCount: resolvedFilters.length
      })
    }

    return data as SearchGroup[]
  }

  /**
   * @inheritDoc
   */
  public setConfig (): void {
    const config = this.siteService.getProperty('search')
    this.setInitialData(config.initialData)
    this.mergeConfig(config.config)
  }

  /**
   * Merges the passed-in config with existing config.
   * This merges just a `label`, so we `component` for group should not be overridden.
   */
  protected mergeConfig (config: SearchResultsConfig): void {
    if (Object.keys(config).length < 1) {
      logger('[SearchService]: Could not merge config, given config either is empty or undefined', 'warn')
      return
    }

    for (const tab in this._tabs) {
      const foundConfigKey = (Object.keys(this._tabs[tab].preview)).find((key) => {
        return Object.keys(config).includes(key)
      })

      if (typeof foundConfigKey !== 'undefined') {
        this._tabs[tab] = {
          ...this._tabs[tab],
          preview: {
            [foundConfigKey]: {
              ...this._tabs[tab].preview[foundConfigKey],
              label: config[foundConfigKey].label ?? this._tabs[tab].preview[foundConfigKey].label
            }
          }
        }
      } else {
        this._tabs[tab] = {
          ...this._tabs[tab],
          preview: config
        }
      }
    }
  }

  /**
   * @inheritDoc
   */
  protected setInitialData (initialSearchGroup: SearchGroup): void {
    this.initialData = initialSearchGroup
  }

  /**
   * @inheritDoc
   * @private
   */
  private static resolveFilters (response: IResponse): Array<FilterParamConfig> | undefined {
    if (response.data.meta.related[PossibleRelatedType.ProductFilter].length === 0) {
      return
    }

    return response.data.meta.related[PossibleRelatedType.ProductFilter]
  }
}
